import { HttpClient } from '../http/HttpClient';
import { HttpError } from '../http/HttpError';

/**
 * Wizard API Service - Frontend service for communicating with Spring Boot backend
 */
export class WizardApiService {
    constructor(config = {}) {
        // Initialize HTTP client
        this.httpClient = new HttpClient({
            baseUrl: config.baseUrl || 'https://app.legalstream.org/api/wizards',
            // baseUrl: config.baseUrl || 'http://localhost:8081/api/wizards',
            onRequest: this.logRequest.bind(this),
            onResponse: this.logResponse.bind(this),
            onError: this.logError.bind(this),
        });
    }

    /**
     * Transform API response to match expected structure
     * @private
     */
    _transformWizardResponse(apiWizard) {
        try {
            // Your API returns data wrapped in a response object
            // Extract the actual wizard from data property if it exists
            const wizardData = apiWizard.data || apiWizard;

            // Parse the wizardConfig string if it exists
            let wizardConfig = {};
            if (wizardData.wizardConfig) {
                wizardConfig = typeof wizardData.wizardConfig === 'string'
                    ? JSON.parse(wizardData.wizardConfig)
                    : wizardData.wizardConfig;
            }

            // Return the transformed structure matching your create payload
            return {
                id: wizardData.id,
                path: wizardData.path,
                name: wizardData.name || wizardConfig.name || '',
                description: wizardData.description || wizardConfig.description || '',
                sections: wizardConfig.sections || wizardConfig.jsonSchema?.sections || [],
                created: wizardConfig.created || wizardData.createdAt,
                updated: wizardConfig.updated || wizardData.updatedAt,
                jsonSchema: wizardConfig.jsonSchema || {},
                createdBy: wizardData.createdBy,
                modifiedBy: wizardData.modifiedBy,
                // Keep backend metadata
                _backendData: {
                    createdAt: wizardData.createdAt,
                    updatedAt: wizardData.updatedAt,
                    version: wizardData.version,
                    createdBy: wizardData.createdBy,
                    modifiedBy: wizardData.modifiedBy,
                    wizardConfig: wizardData.wizardConfig // Keep original string
                }
            };
        } catch (error) {
            console.error('Error transforming wizard response:', error);
            // Return a safe fallback structure
            return {
                id: (apiWizard.data || apiWizard).id || '',
                name: (apiWizard.data || apiWizard).name || '',
                path: (apiWizard.data || apiWizard).path || '',
                description: (apiWizard.data || apiWizard).description || '',
                sections: [],
                created: (apiWizard.data || apiWizard).createdAt,
                updated: (apiWizard.data || apiWizard).updatedAt,
                jsonSchema: {},
                _backendData: {
                    createdAt: (apiWizard.data || apiWizard).createdAt,
                    updatedAt: (apiWizard.data || apiWizard).updatedAt,
                    version: (apiWizard.data || apiWizard).version || 1,
                    createdBy: (apiWizard.data || apiWizard).createdBy || '',
                    modifiedBy: (apiWizard.data || apiWizard).modifiedBy || ''
                }
            };
        }
    }

    /**
     * Get all wizards (sorted by updated date)
     */
    async getAllWizards() {
        console.group('API Request: GET /api/wizards');

        try {
            console.info('Fetching all wizards from server...');

            const apiWizards = await this.httpClient.get('');

            console.debug('Raw API response:', apiWizards);

            // apiWizards is already the array (HttpClient extracts data.data)
            const wizardArray = Array.isArray(apiWizards) ? apiWizards : [];

            // Transform each wizard to match expected structure
            const transformedWizards = wizardArray.map(wizard => this._transformWizardResponse(wizard));

            console.info(`Successfully fetched ${transformedWizards.length} wizards`);

            if (transformedWizards.length > 0) {
                console.debug('First wizard:', transformedWizards[0]);
            }

            return transformedWizards;
        } catch (error) {
            console.error('Failed to fetch wizards:', error.toString());
            console.error('Error details:', error);

            throw this.enhanceError(error, 'Failed to fetch wizards');
        } finally {
            console.groupEnd();
        }
    }

    /**
     * Get single wizard by ID
     */
    async getWizard(id) {
        console.group(`API Request: GET /api/wizards/${id}`);

        try {
            console.info(`Fetching wizard with ID: ${id}`);

            const apiResponse = await this.httpClient.get(`/${id}`);


            // Your API returns {success, message, data, status, timestamp}
            const apiWizard = apiResponse.data || apiResponse;

            // Transform the wizard to match expected structure
            const transformedWizard = this._transformWizardResponse(apiWizard);

            console.info('Wizard fetched successfully');
            console.debug('Wizard data:', transformedWizard);

            return transformedWizard;
        } catch (error) {
            console.error('Failed to fetch wizard:', error.toString());

            if (error.status === 404) {
                console.warn(`Wizard with ID ${id} not found on server`);
            }

            throw this.enhanceError(error, `Failed to fetch wizard with ID: ${id}`);
        } finally {
            console.groupEnd();
        }
    }

    /**
     * Create new wizard
     */
    async createWizard(wizardData) {
        console.group('API Request: POST /api/wizards');

        try {
            console.info('Creating new wizard...');
            console.debug('Request data:', wizardData);

            const attribute = await this.httpClient.getCurrentUserAttributes()

            // Prepare request body matching backend DTO
            const requestBody = {
                wizardConfig: typeof wizardData === 'string' ? wizardData : JSON.stringify(wizardData),
                path: wizardData.path,
                createdBy: attribute?.email || 'system',
                modifiedBy: attribute?.email || 'system',
                name: wizardData.name || '',
                description: wizardData.description || ''
            };

            console.debug('Request body:', requestBody);

            const apiResponse = await this.httpClient.post('', requestBody);

            // Your API returns {success, message, data, status, timestamp}
            const createdWizard = apiResponse.data || apiResponse;

            // Transform the response
            const transformedWizard = this._transformWizardResponse(createdWizard);

            console.info('Wizard created successfully');
            console.debug('Created wizard:', transformedWizard);

            return transformedWizard;
        } catch (error) {
            console.error('Failed to create wizard:', error.toString());
            console.error('Error details:', error);

            throw this.enhanceError(error, 'Failed to create wizard');
        } finally {
            console.groupEnd();
        }
    }

    /**
     * Update existing wizard
     */
    async updateWizard(id, wizardData) {
        console.group(`API Request: PUT /api/wizards/${id}`);

        try {
            console.info(`Updating wizard with ID: ${id}`);
            console.debug('Update data:', wizardData);

            const attribute = await this.httpClient.getCurrentUserAttributes()

            // Prepare request body
            const requestBody = {
                wizardConfig: typeof wizardData === 'string' ? wizardData : JSON.stringify(wizardData),
                modifiedBy: attribute?.email || 'system',
                path: wizardData.path,
                name: wizardData.name || '',
                description: wizardData.description || ''
            };

            console.debug('Request body:', requestBody);

            const apiResponse = await this.httpClient.put(`/${id}`, requestBody);

            // Your API returns {success, message, data, status, timestamp}
            const updatedWizard = apiResponse.data || apiResponse;

            // Transform the response
            const transformedWizard = this._transformWizardResponse(updatedWizard);

            console.info('Wizard updated successfully');
            console.debug('Updated wizard:', transformedWizard);

            return transformedWizard;
        } catch (error) {
            console.error('Failed to update wizard:', error.toString());
            console.error('Error details:', error);

            throw this.enhanceError(error, `Failed to update wizard with ID: ${id}`);
        } finally {
            console.groupEnd();
        }
    }

    /**
     * Delete wizard by ID
     */
    async deleteWizard(id) {
        console.group(`API Request: DELETE /api/wizards/${id}`);

        try {
            console.info(`Deleting wizard with ID: ${id}`);

            const apiResponse = await this.httpClient.delete(`/${id}`);

            console.info('Wizard deleted successfully');

            return apiResponse.success || true;
        } catch (error) {
            console.error('Failed to delete wizard:', error.toString());

            if (error.status === 404) {
                console.warn(`Wizard with ID ${id} not found on server`);
            }

            throw this.enhanceError(error, `Failed to delete wizard with ID: ${id}`);
        } finally {
            console.groupEnd();
        }
    }

    /**
     * Save/Update wizard (unified method for compatibility)
     */
    async saveWizard(wizardData) {
        delete wizardData.sections;
        if (wizardData.id) {
            return this.updateWizard(wizardData.id, wizardData);
        } else {
            return this.createWizard(wizardData);
        }
    }

    /**
     * Validate wizard configuration JSON
     */
    validateWizardConfig(wizardConfig) {
        try {
            if (typeof wizardConfig === 'string') {
                JSON.parse(wizardConfig);
            } else {
                JSON.stringify(wizardConfig);
            }
            return true;
        } catch (error) {
            throw HttpError.validationError(`Invalid wizard configuration: ${error.message}`);
        }
    }

    /**
     * Logging methods (updated to remove retry attempt parameter)
     */
    logRequest({ url, config }) {
        console.debug(`Request: ${config.method} ${url}`);

        if (config.body && config.method !== 'GET') {
            console.debug('Request body:', config.body);
        }
    }

    logResponse({ url, config, response }) {
        console.debug(`Response: ${config.method} ${url} - ${response.status}`);
    }

    logError({ url, config, error }) {
        console.error(`Error: ${config.method} ${url} - ${error.message}`);
    }

    /**
     * Enhance error with additional context
     */
    enhanceError(error, context) {
        if (error instanceof HttpError) {
            error.context = context;
            return error;
        }

        return new HttpError(
            error.status || 500,
            `${context}: ${error.message}`,
            { originalError: error }
        );
    }

    /**
     * Test connection to backend
     */
    async testConnection() {
        try {
            console.info('Testing connection to wizard API...');

            const response = await fetch(`${this.httpClient.baseUrl}`, {
                method: 'HEAD',
                headers: this.httpClient.defaultHeaders,
            });

            const isConnected = response.ok;
            console.info(isConnected ? 'Connection successful' : 'Connection failed');

            return {
                connected: isConnected,
                status: response.status,
                timestamp: new Date().toISOString(),
            };
        } catch (error) {
            console.error('Connection test failed:', error);
            return {
                connected: false,
                error: error.message,
                timestamp: new Date().toISOString(),
            };
        }
    }

    /**
     * Get wizard summaries - List of all wizards with name, url, and id only
     * Calls the new /summaries endpoint
     */
    async getWizardSummaries() {
        console.group('API Request: GET /api/wizards/summaries');

        try {
            console.info('Fetching wizard summaries from server...');

            // Call the new summaries endpoint
            const summariesResponse = await this.httpClient.get('/summaries');

            console.debug('Raw summaries response:', summariesResponse);

            // summariesResponse should be an array of {id, name, url}
            const summariesArray = Array.isArray(summariesResponse) ? summariesResponse : [];

            console.info(`Successfully fetched ${summariesArray.length} wizard summaries`);

            if (summariesArray.length > 0) {
                console.debug('First summary:', summariesArray[0]);
            }

            return summariesArray;
        } catch (error) {
            console.error('Failed to fetch wizard summaries:', error.toString());
            console.error('Error details:', error);

            // If /summaries endpoint doesn't exist, fall back to getAllWizards
            if (error.status === 404) {
                console.warn('Summaries endpoint not found, falling back to getAllWizards');
                return this.getAllWizards().then(wizards =>
                    wizards.map(wizard => ({
                        id: wizard.id,
                        name: wizard.name,
                        url: wizard.path ? this.constructFullUrl(wizard.path) : ''
                    }))
                );
            }

            throw this.enhanceError(error, 'Failed to fetch wizard summaries');
        } finally {
            console.groupEnd();
        }
    }

    /**
     * Set the function to get current user attributes
     * This should be called by the application after authentication
     */
    setUserAttributesGetter(getUserAttributesFn) {
        this.getCurrentUserAttributes = getUserAttributesFn;
        console.info('User attributes getter set for WizardApiService');
    }
}